import 'chartjs-adapter-moment';
import queryString from 'query-string';
import { useEffect, useRef, useState } from 'react';
import { Card, Col, Container, ListGroup, ListGroupItem, Pagination, Placeholder, Row } from "react-bootstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import { callQuery, USER_EVENTS_RECENT_SESSIONS, USER_EVENTS_TREND } from '../../../amplify/appsync/query';
import { getLocalDateTimeStr, getSqlDateStrFromDateStr } from '../../../utils/time';
import { DateType } from '../../filters/dateTypes';
import { METRICS_PERIOD_OPTIONS } from '../../filters/metricsFilterOptions';
import { getMetricsLineOpts } from '../../ops/metricsPlotOpts';
import { AdminUserEventsReportFilterBar } from './AdminUserEventsReportFilterBar';
import { Line } from 'react-chartjs-2';
import { AdminUserEventsReportDetailsModal } from './AdminUserEventsReportDetailsModal';


export const USER_EVENTS_EVENT_TYPE_OPTIONS = [
    {
        "name": "Page Views",
        "id": "page_view",
    },
    {
        "name": "Button Clicks",
        "id": "button_click",
    },
];

export const RECENT_SESSIONS_COLUMNS = [
    {
        name: "Session ID",
        size: 4,
        id: "session_id"
    },
    {
        name: "Username",
        id: "username",
        size: 4,
    },
    {
        name: "Number of Events",
        id: "num_events",
        size: 2,
    },
    {
        name: "Latest Event Time",
        id: "latest_event_time",
        size: 2,
    },
];

const PAGE_SIZE = 30;

export const AdminUserEventsReport = (props: {
    user: any,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryModel = queryString.parse(location.search);

    const chartRef = useRef<any>(null);
    const [hoverPosition, setHoverPosition] = useState<Date>();

    const [showSessionDetailsModal, setShowSessionDetailsModal] = useState(false);
    const [sessionId, setSessionId] = useState<string>();

    const [plotWidth, setPlotWidth] = useState(1024);
    const plotWidthRef = useRef(plotWidth);
    const target = useRef(null);
    const [confirmationTime, setConfirmationTime] = useState<Date>(new Date());
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);

    const [filterDateOptions, setFilterDateOptions]: any = useState<any>({
        dateOptionType: DateType.Relative,
        relativeStart: 7,
        relativeStartScale: "d",
        relativeEnd: 0,
        relativeEndScale: "d",
        absoluteStart: queryModel["start"] ? new Date(Number(queryModel["start"])) : new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 7),
        absoluteEnd: queryModel["end"] ? new Date(Number(queryModel["end"])) : new Date(),
    })

    const [filterOptions, setFilterOptions]: any = useState<any>({
        "period": queryModel["period"] ? queryModel["period"] : 1,
        "event_type": queryModel["event_type"] ? queryModel["event_type"] : 0,
    });

    const [isLoadingTrend, setIsLoadingTrend] = useState(false);
    const [trendData, setTrendData] = useState<any>();
    const [trendOptions, setTrendOptions] = useState<any>();

    const [isLoadingRecentSessions, setIsLoadingRecentSessions] = useState(false);
    const [recentSessions, setRecentSessions] = useState<any[]>([]);
    const [activePageNumber, setActivePageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const [totalNumberOfResults, setTotalNumberOfResults] = useState<number>(0);
    const [columns, setColumns] = useState<any[]>(RECENT_SESSIONS_COLUMNS);

    useEffect(() => {
        refresh();
    }, [activePageNumber]);

    const range = (start: number, stop: number, step: number) => {
        if (typeof stop == "undefined") {
            // one param defined
            stop = start;
            start = 0;
        }

        if (typeof step == "undefined") {
            step = 1;
        }

        if ((step > 0 && start >= stop) || (step < 0 && start <= stop)) {
            return [];
        }

        var result = [];
        for (var i = start; step > 0 ? i < stop : i > stop; i += step) {
            result.push(i);
        }

        return result;
    };

    const pageUpdate = (nextPageInput: number) => {
        let nextPage = nextPageInput;
        nextPage = Math.min(totalPages, nextPage);
        nextPage = Math.max(1, nextPage);
        setActivePageNumber(nextPage);
    };

    const updateFilterOptions = (option: string, val: any) => {
        setFilterOptions({
            ...filterOptions,
            [option]: val,
        });
    }

    const refresh = () => {
        setIsLoadingTrend(true);
        setIsLoadingRecentSessions(true);

        callQuery({
            operation: USER_EVENTS_TREND,
            period: METRICS_PERIOD_OPTIONS[filterOptions["period"]].id,
            start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            event_type: USER_EVENTS_EVENT_TYPE_OPTIONS[filterOptions["event_type"]].id,
        }).then((data: any) => {
            // console.log("USER_EVENTS_TREND");
            // console.log(data);
            processMetricsData(data);
        }).finally(() => {
            setIsLoadingTrend(false);
        });

        callQuery({
            operation: USER_EVENTS_RECENT_SESSIONS,
            start: getSqlDateStrFromDateStr(filterDateOptions.absoluteStart),
            end: getSqlDateStrFromDateStr(filterDateOptions.absoluteEnd),
            offset: (activePageNumber - 1) * PAGE_SIZE,
            limit: PAGE_SIZE,
        }).then((promiseOutput: any) => {
            const results = promiseOutput["results"];
            const totalNum = promiseOutput["total"];
            setRecentSessions(results);
            const totalPages = Math.ceil(
                Number(totalNum) / PAGE_SIZE
            );
            setTotalPages(totalPages);
            setTotalNumberOfResults(totalNum);
        }).finally(() => {
            setIsLoadingRecentSessions(false);
        });
    }

    const formatRowVal = (col: any, row: any) => {
        if (col.id.endsWith("_time")) {
            return getLocalDateTimeStr(row[col.id]);
        } else {
            return row[col.id];
        }
    }

    const processMetricsData = (metrics: any) => {

        if (!metrics || Object.keys(metrics).length === 0) {
            setTrendData({});
            setTrendOptions({});
            return;
        }

        let data: any = {
            datasets: [],
        };
        let opts: any = {};

        //
        // Format for Chart.js
        //
        data.datasets.push({
            data: [],
            label: "Page Views",
            borderColor: "#1f77b4",
            backgroundColor: "#1f77b4",
            hoverBorderColor: "blue",
            hoverBackgroundColor: "blue",
            fill: false,
            // spanGaps: 1000 * 60 * 60 * 1, // Setting this will cause the shaded region to disappear at day granularity
            borderWidth: 1,
            hoverBorderWidth: 2,
            pointRadius: 1,
            pointHoverRadius: 2,
        });
        // Add one day to the end to account for forecasting
        opts = getMetricsLineOpts(false, filterDateOptions.absoluteStart, new Date(filterDateOptions.absoluteEnd.getTime() + (1000 * 60 * 60 * 24)), chartRef, hoverPosition, setHoverPosition, setConfirmationTime, setShowConfirmationModal);

        //
        // Add the data for the main plot
        //
        for (let metric of metrics) {
            data.datasets[0].data.push({
                x: new Date(metric.timestamp),
                y: metric.cnt,
            })
        }

        setTrendData(data);
        setTrendOptions(opts);
    }

    return (
        <div>
            <div className="metrics-sidebar" style={{overflowY: 'scroll'}}>
                <AdminUserEventsReportFilterBar
                    isLoading={isLoadingTrend}
                    filterOptions={filterOptions}
                    updateFilterOptions={updateFilterOptions}
                    filterDateOptions={filterDateOptions}
                    setFilterDateOptions={setFilterDateOptions}
                    refresh={refresh}
                    user={props.user}
                />
            </div>
            <Container fluid className="metrics-container">
                <Row>
                    <Col>
                        <h5>User Metrics</h5>
                    </Col>
                </Row>
                <Row className="mt-2">
                    <Col>
                        <h6>Page Views</h6>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col className='text-right'>
                        <div>
                            {
                                trendData && trendOptions && (
                                    <div>
                                        {
                                            // JSON.stringify(trendData)
                                            // @ts-ignore
                                            <Line options={trendOptions} data={trendData} height="240px" ref={chartRef} />
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </Col>
                </Row>
                <hr />
                <Row className="mt-2">
                    <Col>
                        <h6>Recent Sessions</h6>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col>
                        <Card className="trackboard-table">
                            {isLoadingRecentSessions ? (
                                <Row className="m-4">
                                    <Col>
                                        {[1, 2, 3].map((v) => (
                                            <Row>
                                                <Col>
                                                    <Placeholder
                                                        key={v}
                                                        xs={6}
                                                    />
                                                </Col>
                                            </Row>
                                        ))}
                                    </Col>
                                </Row>
                            ) : (
                                <>
                                    <ListGroup variant="flush">
                                        <ListGroupItem className="d-flex flex-column trackboard-table-header">
                                            <div
                                                style={{
                                                    display: "block",
                                                    width: "100%",
                                                }}
                                            >
                                                {columns.map((col) => {
                                                    const totalRelativeCols =
                                                        columns.reduce(
                                                            (acc, obj) =>
                                                                acc + (obj.size ?? 0),
                                                            0
                                                        );
                                                    return (
                                                        <div
                                                            key={col.id}
                                                            style={{
                                                                display:
                                                                    "inline-block",
                                                                cursor: "pointer",
                                                                width: `${
                                                                    (100 *
                                                                        col.size) /
                                                                    totalRelativeCols
                                                                }%`,
                                                            }}
                                                            onClick={() => {
                                                                // sortPatients(col.id)
                                                            }}
                                                        >
                                                            {col.name}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </ListGroupItem>
                                        {
                                            recentSessions && recentSessions.map(row => (
                                                <ListGroupItem className="d-flex flex-column" action onClick={() => {
                                                    setSessionId(row.session_id);
                                                    setShowSessionDetailsModal(true);
                                                }}>
                                                    <div
                                                        style={{
                                                            display: "block",
                                                            width: "100%",
                                                        }}
                                                    >
                                                        {columns.map((col) => {
                                                            const totalRelativeCols =
                                                                columns.reduce(
                                                                    (acc, obj) =>
                                                                        acc + (obj.size ?? 0),
                                                                    0
                                                                );
                                                            return (
                                                                <div
                                                                    key={col.id}
                                                                    style={{
                                                                        display: "inline-block",
                                                                        cursor: "pointer",
                                                                        width: `${
                                                                            (100 * col.size) / totalRelativeCols
                                                                        }%`,
                                                                    }}
                                                                >
                                                                    {
                                                                        formatRowVal(col, row)
                                                                    }
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </ListGroupItem>
                                            ))
                                        }
                                    </ListGroup>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
                <AdminUserEventsReportDetailsModal showModal={showSessionDetailsModal} setShowModal={setShowSessionDetailsModal} sessionId={sessionId} />
                {
                    !isLoadingRecentSessions && (
                        <Row>
                            <Col md={8}>
                                <Pagination>
                                    <Pagination.First
                                        onClick={() => setActivePageNumber(1)}
                                    />
                                    <Pagination.Prev
                                        onClick={() =>
                                            setActivePageNumber(
                                                Math.max(1, activePageNumber - 1)
                                            )
                                        }
                                    />

                                    {totalPages < 6 ? (
                                        <>
                                            {range(1, totalPages + 1, 1).map(
                                                (idx: number) => {
                                                    if (idx === activePageNumber)
                                                        return (
                                                            <Pagination.Item
                                                                key={idx}
                                                                active
                                                                onClick={() =>
                                                                    pageUpdate(idx)
                                                                }
                                                            >
                                                                {idx}
                                                            </Pagination.Item>
                                                        );
                                                    else
                                                        return (
                                                            <Pagination.Item
                                                                key={idx}
                                                                onClick={() =>
                                                                    pageUpdate(idx)
                                                                }
                                                            >
                                                                {idx}
                                                            </Pagination.Item>
                                                        );
                                                }
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {activePageNumber - 4 > 1 && (
                                                <Pagination.Ellipsis />
                                            )}
                                            {range(
                                                Math.max(1, activePageNumber - 4),
                                                Math.min(
                                                    totalPages,
                                                    activePageNumber + 5
                                                ),
                                                1
                                            ).map((idx: number) => {
                                                if (idx === activePageNumber)
                                                    return (
                                                        <Pagination.Item
                                                            key={idx}
                                                            active
                                                            onClick={() =>
                                                                pageUpdate(idx)
                                                            }
                                                        >
                                                            {idx}
                                                        </Pagination.Item>
                                                    );
                                                else
                                                    return (
                                                        <Pagination.Item
                                                            key={idx}
                                                            onClick={() =>
                                                                pageUpdate(idx)
                                                            }
                                                        >
                                                            {idx}
                                                        </Pagination.Item>
                                                    );
                                            })}
                                            {activePageNumber + 5 < totalPages + 1 && (
                                                <Pagination.Ellipsis />
                                            )}
                                        </>
                                    )}
                                    <Pagination.Next
                                        onClick={() =>
                                            pageUpdate(
                                                Math.min(
                                                    totalPages,
                                                    activePageNumber + 1
                                                )
                                            )
                                        }
                                    />
                                    <Pagination.Last
                                        onClick={() =>
                                            pageUpdate(Math.max(totalPages, 1))
                                        }
                                    />
                                </Pagination>
                            </Col>
                            <Col md={4} className='text-right text-muted'>
                                Results Found: {totalNumberOfResults}
                            </Col>
                        </Row>
                    )
                }
            </Container>
        </div>
    );
};
